import { useState, useEffect } from 'preact/hooks';
import { superAdminFetch, superAdminPost } from '../../lib/super-admin-api';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.unplug-app.com';

export function SuperAdminWorkspaces() {
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchWorkspaces();
  }, [filter]);

  async function fetchWorkspaces() {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filter !== 'all') params.set('status', filter);

      const res = await superAdminFetch(`${API_URL}/api/super-admin/workspaces?${params}`);
      if (!res.ok) throw new Error('Failed to fetch workspaces');

      const data = await res.json();
      setWorkspaces(data.workspaces || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredWorkspaces = workspaces.filter(ws => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      ws.name?.toLowerCase().includes(searchLower) ||
      ws.domain?.toLowerCase().includes(searchLower) ||
      ws.id?.toLowerCase().includes(searchLower)
    );
  });

  async function handleExtendTrial(workspaceId, days) {
    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${workspaceId}/extend-trial`, { days });

      if (!res.ok) throw new Error('Failed to extend trial');

      fetchWorkspaces();
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleActivate(workspaceId) {
    if (!confirm('Activate this subscription manually?')) return;

    try {
      const res = await superAdminPost(`${API_URL}/api/super-admin/workspaces/${workspaceId}/activate`, { months: 12 });

      if (!res.ok) throw new Error('Failed to activate');

      fetchWorkspaces();
    } catch (err) {
      alert(err.message);
    }
  }

  if (error) {
    return (
      <div class="super-admin-page">
        <div class="error-state">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div class="super-admin-page">
      <div class="page-header">
        <div>
          <h1>Workspaces</h1>
          <p class="text-muted">Manage all workspace subscriptions</p>
        </div>
        <a href="/super-admin" class="btn btn-secondary">Back to Dashboard</a>
      </div>

      {/* Filters */}
      <div class="card">
        <div class="card-body">
          <div class="filters-row">
            <div class="filter-group">
              <label>Status</label>
              <select value={filter} onChange={e => setFilter(e.target.value)}>
                <option value="all">All</option>
                <option value="trialing">Trialing</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
                <option value="cancelled">Cancelled</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>
            <div class="filter-group flex-grow">
              <label>Search</label>
              <input
                type="text"
                placeholder="Search by name, domain, or ID..."
                value={search}
                onInput={e => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Workspaces Table */}
      <div class="card mt-4">
        <div class="card-body">
          {loading ? (
            <div class="loading-state">
              <div class="spinner" />
              <p>Loading workspaces...</p>
            </div>
          ) : (
            <table class="table">
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Status</th>
                  <th>Plan</th>
                  <th>Trial/Expiry</th>
                  <th>Users</th>
                  <th>Discount</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkspaces.map(ws => (
                  <tr key={ws.id}>
                    <td>
                      <div class="workspace-info">
                        {ws.slack_team_icon && (
                          <img src={ws.slack_team_icon} alt="" class="workspace-icon" />
                        )}
                        <div>
                          <strong>{ws.name}</strong>
                          <br />
                          <span class="text-muted text-sm">{ws.id}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class={`badge badge-${getStatusColor(ws.subscription?.status)}`}>
                        {ws.subscription?.status || 'none'}
                      </span>
                    </td>
                    <td>{ws.subscription?.plan || 'none'}</td>
                    <td>
                      {ws.subscription?.status === 'trialing' && ws.subscription?.trial_ends_at && (
                        <div>
                          <span class={getDaysRemaining(ws.subscription.trial_ends_at) <= 3 ? 'text-danger' : ''}>
                            {getDaysRemaining(ws.subscription.trial_ends_at)} days left
                          </span>
                          <br />
                          <span class="text-muted text-sm">
                            Ends {formatDate(ws.subscription.trial_ends_at)}
                          </span>
                        </div>
                      )}
                      {ws.subscription?.status === 'active' && ws.subscription?.subscription_ends_at && (
                        <div>
                          <span>Renews {formatDate(ws.subscription.subscription_ends_at)}</span>
                        </div>
                      )}
                      {!ws.subscription?.trial_ends_at && !ws.subscription?.subscription_ends_at && '-'}
                    </td>
                    <td>
                      {ws.user_count || 0}
                      {ws.subscription?.seats_limit && (
                        <span class="text-muted"> / {ws.subscription.seats_limit}</span>
                      )}
                    </td>
                    <td>
                      {ws.subscription?.discount_percent > 0 ? (
                        <span class="badge badge-info">{ws.subscription.discount_percent}%</span>
                      ) : '-'}
                    </td>
                    <td>
                      <div class="action-buttons">
                        <a href={`/super-admin/workspaces/${ws.id}`} class="btn btn-sm btn-primary">
                          View
                        </a>
                        {ws.subscription?.status === 'trialing' && (
                          <button
                            class="btn btn-sm btn-secondary"
                            onClick={() => handleExtendTrial(ws.id, 7)}
                          >
                            +7 Days
                          </button>
                        )}
                        {(ws.subscription?.status === 'trialing' || ws.subscription?.status === 'expired') && (
                          <button
                            class="btn btn-sm btn-success"
                            onClick={() => handleActivate(ws.id)}
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filteredWorkspaces.length === 0 && (
                  <tr>
                    <td colspan="7" class="text-center text-muted">
                      No workspaces found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'active': return 'success';
    case 'trialing': return 'warning';
    case 'expired':
    case 'cancelled': return 'danger';
    case 'suspended': return 'danger';
    default: return 'secondary';
  }
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}

function getDaysRemaining(dateStr) {
  if (!dateStr) return 0;
  const endDate = new Date(dateStr);
  const now = new Date();
  return Math.max(0, Math.ceil((endDate - now) / (1000 * 60 * 60 * 24)));
}
